import React, { forwardRef, useCallback, useState } from "react";

import { getEnvironment, useTranslation } from "utils";

import { TILLAGE_BLANK_PRACTICE } from "consts";

import { EDITING_REQUEST_TYPE, PracticeFCPO, VerificationEditingRequest, ZERO_UUID } from "models";

import { Flex, Form, Icon, Link, Typography } from "components";

import { TillageApplicationForm } from "pages/app/all-field-view/components/forms/tillage/TillageApplicationForm";
import { TillageYesNoForm } from "pages/app/all-field-view/components/forms/tillage/TillageYesNoForm";
import { determinePracticeSettings } from "pages/app/all-field-view/utils/AllFieldsUtils.ts";
import { PracticeConfigForBulk } from "pages/app/all-field-view/utils/BulkDataEntryUtils.ts";
import { InputRequirementConfig } from "pages/app/all-field-view/utils/getInputRequirementConfig";
import { EditingRequestBanner } from "pages/app/field-timeline/components/EditingRequestBanner";

export type AddEditTillageFormProps = {
  cropId: string;
  disabled: boolean;
  existingTillages: PracticeFCPO[];
  baselineYears: number[] | undefined;
  year: number;
  recommendedSeedingDate?: string;
  requirementConfig: InputRequirementConfig | null;
  optionalInputsAreShown: boolean;
  practiceConfigForBulk?: PracticeConfigForBulk;
  bulkEditMode?: boolean;
  requirePreciseTillageDepth: boolean;
  // forwarded to each entry so bulk flows can label which crops it applies to
  renderEntryTag?: (practice: PracticeFCPO) => React.ReactNode;
  // forwarded so bulk flows can rename an entry that does not exist on any crop yet
  renderEntryTitle?: (practice: PracticeFCPO) => React.ReactNode;
  requireTillageSoilMixing?: boolean;
  // not offered in bulk edit flows; those callers simply omit these
  editingRequests?: VerificationEditingRequest[];
  isSubmissionFeatureEnabled?: boolean;
  farmId?: string;
  // locks every input of every entry, whatever its own verification state allows — for a caller that may not write to it at all
  isReadOnly?: boolean;
};

export const AddEditTillageForm = forwardRef<HTMLDivElement, AddEditTillageFormProps>(
  (
    {
      cropId,
      existingTillages,
      baselineYears,
      disabled,
      year,
      recommendedSeedingDate,
      requirementConfig,
      optionalInputsAreShown,
      practiceConfigForBulk,
      bulkEditMode,
      requirePreciseTillageDepth,
      renderEntryTag,
      renderEntryTitle,
      requireTillageSoilMixing,
      editingRequests = [],
      isSubmissionFeatureEnabled = false,
      farmId,
      isReadOnly,
    }: AddEditTillageFormProps,
    ref
  ) => {
    const { t } = useTranslation(["allFieldView"]);
    const form = Form.useFormInstance();

    const { defaultToggleState, existingPractices, toggleDisabled, hasMixedValues } = determinePracticeSettings({
      practiceConfigForBulk: practiceConfigForBulk,
      practices: existingTillages,
      disabled,
    });

    const [tillageApplications, setTillageApplications] = useState(() => existingPractices);
    const [isShowingTillageOptions, setIsShowingTillageOptions] = useState<boolean | null>(defaultToggleState);

    const addNewTillageApplication = useCallback(() => {
      setTillageApplications((prev) => [...prev, TILLAGE_BLANK_PRACTICE(cropId)]);
    }, [cropId]);

    const deleteTillageApplication = (index: number) => {
      setTillageApplications((prev) => {
        const updated = [...prev];
        updated.splice(index, 1);

        // update form
        const values = form.getFieldsValue(true);
        for (let i = index; i < updated.length; i += 1) {
          values[`tillage-${i}`] = values[`tillage-${i + 1}`];
        }
        delete values[`tillage-${updated.length}`];
        form.setFieldsValue(values);

        // update UI
        if (updated.length === 0) {
          form.setFieldValue("isTillageApplied", null);
          setIsShowingTillageOptions(null);
        }
        return updated;
      });
    };

    const handleTillageYesNoToggle = useCallback(
      (value: boolean) => {
        setIsShowingTillageOptions(() => {
          // if the first application is blank and the user chooses "Yes", add a new tillage application.
          if (tillageApplications.length === 0 && value === true) {
            setTillageApplications([TILLAGE_BLANK_PRACTICE(cropId)]);
          }
          return value;
        });
      },
      [cropId, tillageApplications.length]
    );

    const noneAppliedApplication = existingTillages.find((practice) => practice.assignedPracticeId === ZERO_UUID);

    const AddTillageButton = () => (
      <div className="bottom-cta">
        <Link className="bottom-cta button-text" dataTestId="add-edit-tillage-form-add-tillage" onClick={addNewTillageApplication} disabled={disabled}>
          <Flex align="center">
            <Icon iconName="klim/plus" className="plus-icon inline" />
            <Typography variant="paragraph-m">{t("tillageForm.addTillage")}</Typography>
          </Flex>
        </Link>
      </div>
    );

    return (
      <div data-testid="add-edit-tillage-form" ref={ref}>
        <TillageYesNoForm
          value={isShowingTillageOptions}
          onChange={(e) => handleTillageYesNoToggle(e.target.value)}
          tillageApplications={tillageApplications}
          baselineYears={baselineYears}
          disabled={toggleDisabled}
          year={year}
          requirementLevel={requirementConfig?.tillages}
          hasMixedValues={hasMixedValues}
          showMessage={practiceConfigForBulk?.showMessage}
          bulkEditMode={bulkEditMode}
          extraContent={
            isSubmissionFeatureEnabled &&
            !!noneAppliedApplication?.id && (
              <EditingRequest
                farmId={farmId}
                id={noneAppliedApplication.id}
                editingRequest={editingRequests.find((e) => e.fieldTillageId === noneAppliedApplication.id)}
              />
            )
          }
        />

        {isShowingTillageOptions
          ? tillageApplications.map((application, index) => (
              <div key={`tillage-${application.id}-${index}`}>
                <TillageApplicationForm
                  tillage={application}
                  index={index}
                  onDelete={() => deleteTillageApplication(index)}
                  recommendedSeedingDate={recommendedSeedingDate}
                  requirementConfig={requirementConfig}
                  optionalInputsAreShown={optionalInputsAreShown}
                  requirePreciseTillageDepth={requirePreciseTillageDepth}
                  renderEntryTag={renderEntryTag}
                  renderEntryTitle={renderEntryTitle}
                  requireTillageSoilMixing={requireTillageSoilMixing}
                  extraContent={
                    isSubmissionFeatureEnabled &&
                    !!application.id && (
                      <EditingRequest farmId={farmId} id={application.id} editingRequest={editingRequests.find((e) => e.fieldTillageId === application.id)} />
                    )
                  }
                  isReadOnly={isReadOnly}
                />
                {index + 1 === tillageApplications.length ? <AddTillageButton /> : null}
              </div>
            ))
          : null}
      </div>
    );
  }
);

const EditingRequest = ({ farmId, id, editingRequest }: { farmId: string | undefined; id: string; editingRequest: VerificationEditingRequest | undefined }) => {
  if (!farmId || !editingRequest || getEnvironment().admin) {
    return null;
  }

  return <EditingRequestBanner key={EDITING_REQUEST_TYPE.TILLAGE} type={EDITING_REQUEST_TYPE.TILLAGE} farmId={farmId} id={id} requestId={editingRequest.id} />;
};
